import React, { useEffect, useState } from 'react';
import { Merch } from './Merch';
import { CalendarioDisponibilidad } from '../components/CalendarioDisponibilidad';
import { supabase } from '../services/supabaseClient';
import { Calendar, Phone } from 'lucide-react';

interface Slot {
    id: number;
    fecha: string;
    hora_inicio: string;
    hora_fin: string;
    estado: 'disponible' | 'reservado' | 'bloqueado';
    nota: string;
    cliente_nombre: string;
}

export const TunantadaLanding = ({ addToCart, siteConfig }: any) => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch availability slots
    useEffect(() => {
        const fetchSlots = async () => {
            const { data, error } = await supabase
                .from('disponibilidad_tunantada')
                .select('*')
                .order('fecha', { ascending: true });

            if (!error && data) {
                setSlots(data as Slot[]);
            }
        };
        fetchSlots();
    }, []);

    const handleSlotClick = (date: string, slot?: Slot) => {
        if (slot?.estado === 'disponible') {
            setSelectedDate(date);
            setSelectedSlot(slot);
        }
    };

    const closeReservaModal = () => {
        setSelectedDate(null);
        setSelectedSlot(null);
    };

    // Format date for display
    const formatDisplayDate = (dateStr: string) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className="w-full pt-16 min-h-screen bg-mabelita-surface text-mabelita-dark flex flex-col font-sans">
            {/* Hero Tunantada */}
            <div className="relative h-[40vh] md:h-[50vh] bg-mabelita-dark overflow-hidden flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-b from-mabelita-dark/80 via-mabelita-dark/40 to-mabelita-dark/90 z-10"></div>
                <img
                    src="https://images.unsplash.com/photo-1533174000243-c78b87ee3b4e?auto=format&fit=crop&w=1920&q=80"
                    alt="La Tunantada"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 scale-105 animate-[pulse_20s_ease-in-out_infinite_alternate]"
                />
                <div className="relative z-20 text-center px-4 w-full max-w-5xl mx-auto mt-12">
                    <span className="text-mabelita-primary font-bold tracking-[0.2em] uppercase text-sm mb-4 block animate-in fade-in">Patrimonio Cultural de la Nación</span>
                    <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-mabelita-light mb-6 tracking-tight drop-shadow-2xl animate-in slide-in-from-bottom-8 duration-700">
                        La Tunantada
                    </h1>
                    <p className="text-xl md:text-2xl text-mabelita-light/80 font-light tracking-wide max-w-3xl mx-auto animate-in fade-in duration-1000 delay-300 leading-relaxed">
                        Siente el color y la tradición de Jauja en cada pieza. Colección exclusiva en honor a nuestros personajes tradicionales.
                    </p>
                </div>
            </div>

            {/* Calendario de Disponibilidad */}
            <div className="max-w-4xl mx-auto w-full px-4 md:px-8 py-12 md:py-16">
                <div className="text-center mb-10">
                    <span className="inline-flex items-center gap-2 text-sm font-bold text-mabelita-primary tracking-widest uppercase mb-3">
                        <Calendar size={16} />
                        Calendario de Disponibilidad
                    </span>
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3">
                        ¿Cuándo quieres tu Show?
                    </h2>
                    <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                        Consulta nuestras fechas disponibles y solicita tu actuación de Tunantada para fiestas, eventos corporativos, bodas y celebraciones.
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-10">
                    <CalendarioDisponibilidad
                        slots={slots}
                        readOnly={true}
                        onSlotClick={handleSlotClick}
                    />
                </div>
            </div>

            {/* Modal de Reserva */}
            {selectedDate && (
                <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeReservaModal}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Calendar size={28} className="text-emerald-600" />
                            </div>
                            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                                Solicitar Reserva
                            </h3>
                            <p className="text-gray-500 capitalize">
                                {formatDisplayDate(selectedDate)}
                            </p>
                            {selectedSlot && (
                                <p className="text-sm text-emerald-600 font-medium mt-1">
                                    Horario: {selectedSlot.hora_inicio?.slice(0, 5)} - {selectedSlot.hora_fin?.slice(0, 5)}
                                </p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <a
                                href={`https://wa.me/34661122334?text=${encodeURIComponent(`Hola Mabelita, me gustaría reservar una actuación de Tunantada para el ${formatDisplayDate(selectedDate)}. ¿Está disponible?`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                            >
                                <Phone size={18} />
                                Contactar por WhatsApp
                            </a>

                            <button
                                onClick={closeReservaModal}
                                className="w-full py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                        </div>

                        <p className="text-xs text-gray-400 text-center mt-4">
                            La reserva no se confirma hasta recibir respuesta. Nos pondremos en contacto contigo en menos de 24h.
                        </p>
                    </div>
                </div>
            )}

            {/* Grid de Productos Filtrada */}
            <div className="-mt-4 md:-mt-8 z-30 relative px-4 md:px-0 mb-20">
                <Merch
                    addToCart={addToCart}
                    siteConfig={siteConfig}
                    lockedType="tunantada"
                    hideHero={true}
                />
            </div>
        </div>
    );
};
